import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from '../client';
import type { NoteVersion, NoteVersionsResponse } from './types';

export type { NoteVersion };

export function useGetNoteVersions(noteId: string | null) {
  const [versions, setVersions] = useState<NoteVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Lowest content row ID currently loaded — used as cursor for next page
  const oldestIdRef = useRef<number | null>(null);
  // Guard against overlapping fetches
  const fetchingRef = useRef(false);

  const fetchPage = useCallback(
    async (beforeId?: number) => {
      if (!noteId || fetchingRef.current) return;

      fetchingRef.current = true;
      setLoading(true);

      try {
        const url = beforeId
          ? `/app/v1/note/${noteId}/versions?before_id=${beforeId}`
          : `/app/v1/note/${noteId}/versions`;

        const res = await apiGet<NoteVersionsResponse>(url);

        setVersions(prev => (beforeId ? [...prev, ...res.data] : res.data));
        setHasMore(res.has_more);

        if (res.data.length > 0) {
          oldestIdRef.current = Math.min(...res.data.map(v => v.id));
        }
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [noteId],
  );

  // Fetch first page whenever noteId changes (e.g. panel mounts for a new note)
  useEffect(() => {
    if (!noteId) return;
    oldestIdRef.current = null;
    setVersions([]);
    setHasMore(false);
    void fetchPage();
  }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Load the next page using the content row ID cursor. */
  const loadMore = useCallback(() => {
    if (!hasMore || loading || oldestIdRef.current === null) return;
    void fetchPage(oldestIdRef.current);
  }, [hasMore, loading, fetchPage]);

  /**
   * Reset all state back to empty.
   * Useful when the panel stays mounted but its note changes,
   * or to force a fresh reload from the parent.
   */
  const reset = useCallback(() => {
    setVersions([]);
    setHasMore(false);
    setLoading(false);
    oldestIdRef.current = null;
    fetchingRef.current = false;
  }, []);

  /** Re-fetch from page 1, discarding any previously loaded pages. */
  const refresh = useCallback(() => {
    oldestIdRef.current = null;
    fetchingRef.current = false;
    setVersions([]);
    setHasMore(false);
    void fetchPage();
  }, [fetchPage]);

  return { versions, loading, hasMore, loadMore, reset, refresh };
}
